import React, { useState, useEffect, useCallback } from 'react';

interface QuestionnaireData {
  // Section A
  primaryInsured: string;
  spousePartner: string;
  householdMembers: Array<{ name: string; relationship: string; age: string; driver: string; studentAway: string }>;
  primaryAddress: string;
  primaryState: string;
  ownOrRent: string;
  additionalProperties: Array<{ address: string; state: string; type: string; ownedOrRented: string }>;

  // Section B
  homeBasedBusiness: string;
  homeBusinessDescription: string;
  familyOffice: string;
  familyOfficeInsurance: string;
  professionalLicenses: string;
  licenseTypes: string;
  rentalActivity: string;
  rentalPropertyCount: string;

  // Section C
  nonprofitBoard: string;
  nonprofitDO: string;
  nonprofitDetails: string;
  forProfitBoard: string;
  communityAssociationBoard: string;
  communityAssociationDO: string;
  otherFiduciaryRoles: string;
  otherFiduciaryDescription: string;

  // Section D
  coaching: string;
  coachingMinors: string;
  volunteerWork: string;
  volunteerDescription: string;
  mentoring: string;

  // Section E
  politicalActivity: string;
  publicOffice: string;
  politicalActivism: string;

  // Section F
  householdStaff: string;
  staffDetails: Array<{ role: string; fullOrPart: string; w2Or1099: string; count: string }>;
  workersComp: string;

  // Section G
  dogs: string;
  dogDetails: Array<{ breed: string; weight: string; biteHistory: string }>;
  horses: string;
  otherAnimals: string;
  otherAnimalsDescription: string;

  // Section H
  watercraft: string;
  watercraftDetails: Array<{ type: string; length: string; horsepower: string; insured: string }>;
  recreationalVehicles: string;
  recreationalVehiclesDescription: string;
  recreationalVehiclesInsured: string;
  aircraft: string;

  // Section I
  firearms: string;
  firearmsLend: string;

  // Section J
  internationalTravel: string;
  internationalProperty: string;
  internationalPropertyLocation: string;
  internationalBusiness: string;
  internationalBusinessDescription: string;
  internationalEmployees: string;

  // Section K
  socialMedia: string;
  publicProfile: string;

  // Section L
  socialEvents: string;
  eventStaffing: string;

  // Section M
  pendingLitigation: string;
  pendingLitigationDescription: string;
  priorClaims: string;
  priorClaimsDescription: string;
  entityOwnership: string;
  entityDetails: string;
  otherConcerns: string;
  otherConcernsDescription: string;
}

const DEFAULT_DATA: QuestionnaireData = {
  primaryInsured: '', spousePartner: '',
  householdMembers: [{ name: '', relationship: '', age: '', driver: '', studentAway: '' }],
  primaryAddress: '', primaryState: '', ownOrRent: '',
  additionalProperties: [],
  homeBasedBusiness: '', homeBusinessDescription: '',
  familyOffice: '', familyOfficeInsurance: '',
  professionalLicenses: '', licenseTypes: '',
  rentalActivity: '', rentalPropertyCount: '',
  nonprofitBoard: '', nonprofitDO: '', nonprofitDetails: '',
  forProfitBoard: '',
  communityAssociationBoard: '', communityAssociationDO: '',
  otherFiduciaryRoles: '', otherFiduciaryDescription: '',
  coaching: '', coachingMinors: '',
  volunteerWork: '', volunteerDescription: '',
  mentoring: '',
  politicalActivity: '', publicOffice: '', politicalActivism: '',
  householdStaff: '',
  staffDetails: [],
  workersComp: '',
  dogs: '', dogDetails: [],
  horses: '', otherAnimals: '', otherAnimalsDescription: '',
  watercraft: '', watercraftDetails: [],
  recreationalVehicles: '', recreationalVehiclesDescription: '', recreationalVehiclesInsured: '',
  aircraft: '',
  firearms: '', firearmsLend: '',
  internationalTravel: '',
  internationalProperty: '', internationalPropertyLocation: '',
  internationalBusiness: '', internationalBusinessDescription: '',
  internationalEmployees: '',
  socialMedia: '', publicProfile: '',
  socialEvents: '', eventStaffing: '',
  pendingLitigation: '', pendingLitigationDescription: '',
  priorClaims: '', priorClaimsDescription: '',
  entityOwnership: '', entityDetails: '',
  otherConcerns: '', otherConcernsDescription: ''
};

const SECTIONS = [
  { id: 'A', title: 'Household Information', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'B', title: 'Business & Professional', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { id: 'C', title: 'Board Service & Org Roles', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'D', title: 'Volunteer & Community', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { id: 'E', title: 'Political & Public Service', icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9' },
  { id: 'F', title: 'Domestic Employees', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { id: 'G', title: 'Animals & Pets', icon: 'M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5' },
  { id: 'H', title: 'Vehicles & Watercraft', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { id: 'I', title: 'Firearms', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  { id: 'J', title: 'International Activity', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'K', title: 'Social Media & Public Presence', icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
  { id: 'L', title: 'Entertaining & Hosting', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
  { id: 'M', title: 'Special Circumstances', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
];

const STAFF_ROLES = [
  'Nanny / Childcare', 'Housekeeper / Cleaner', 'Gardener / Landscaper',
  'Estate Manager', 'Personal Chef', 'Driver / Chauffeur',
  'Security Personnel', 'Personal Assistant', 'Caretaker (seasonal property)', 'Other'
];

// Radio group helper
const RadioGroup: React.FC<{
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  columns?: number;
}> = ({ name, value, onChange, options, columns = 1 }) => (
  <div className={`grid gap-2 ${columns > 1 ? `grid-cols-${columns}` : ''}`}
    style={columns > 1 ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}>
    {options.map(opt => (
      <label key={opt.value} className="flex items-start gap-2 cursor-pointer group">
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 focus:ring-indigo-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
          {opt.label}
        </span>
      </label>
    ))}
  </div>
);

// Text input helper
const TextInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
}> = ({ label, value, onChange, placeholder, multiline }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
      />
    )}
  </div>
);

interface PersonalUmbrellaQuestionnaireProps {
  onSubmit: (data: Record<string, any>) => void;
}

const PersonalUmbrellaQuestionnaire: React.FC<PersonalUmbrellaQuestionnaireProps> = ({ onSubmit }) => {
  const [data, setData] = useState<QuestionnaireData>(DEFAULT_DATA);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['A']));

  const update = useCallback((field: keyof QuestionnaireData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Emit data on every change
  useEffect(() => {
    // Filter out empty/default values for cleaner data
    const filtered: Record<string, any> = {};
    for (const [key, val] of Object.entries(data)) {
      if (Array.isArray(val)) {
        const nonEmpty = val.filter((item: any) =>
          typeof item === 'object' ? Object.values(item).some(v => v !== '') : item !== ''
        );
        if (nonEmpty.length > 0) filtered[key] = nonEmpty;
      } else if (val !== '') {
        filtered[key] = val;
      }
    }
    onSubmit(filtered);
  }, [data, onSubmit]);

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getSectionCompletionCount = (sectionId: string): number => {
    const fieldMap: Record<string, (keyof QuestionnaireData)[]> = {
      A: ['primaryInsured', 'primaryAddress', 'primaryState', 'ownOrRent'],
      B: ['homeBasedBusiness', 'familyOffice', 'professionalLicenses', 'rentalActivity'],
      C: ['nonprofitBoard', 'forProfitBoard', 'communityAssociationBoard', 'otherFiduciaryRoles'],
      D: ['coaching', 'volunteerWork', 'mentoring'],
      E: ['politicalActivity', 'publicOffice', 'politicalActivism'],
      F: ['householdStaff', 'workersComp'],
      G: ['dogs', 'horses', 'otherAnimals'],
      H: ['watercraft', 'recreationalVehicles', 'aircraft'],
      I: ['firearms'],
      J: ['internationalTravel', 'internationalProperty', 'internationalBusiness', 'internationalEmployees'],
      K: ['socialMedia', 'publicProfile'],
      L: ['socialEvents', 'eventStaffing'],
      M: ['pendingLitigation', 'priorClaims', 'entityOwnership', 'otherConcerns'],
    };
    const fields = fieldMap[sectionId] || [];
    return fields.filter(f => data[f] !== '').length;
  };

  const getSectionTotalFields = (sectionId: string): number => {
    const counts: Record<string, number> = { A: 4, B: 4, C: 4, D: 3, E: 3, F: 2, G: 3, H: 3, I: 1, J: 4, K: 2, L: 2, M: 4 };
    return counts[sectionId] || 1;
  };

  // Dynamic table row helpers
  const addHouseholdMember = () => {
    update('householdMembers', [...data.householdMembers, { name: '', relationship: '', age: '', driver: '', studentAway: '' }]);
  };

  const updateHouseholdMember = (index: number, field: string, value: string) => {
    const updated = [...data.householdMembers];
    updated[index] = { ...updated[index], [field]: value };
    update('householdMembers', updated);
  };

  const removeHouseholdMember = (index: number) => {
    update('householdMembers', data.householdMembers.filter((_, i) => i !== index));
  };

  const addProperty = () => {
    update('additionalProperties', [...data.additionalProperties, { address: '', state: '', type: '', ownedOrRented: '' }]);
  };

  const updateProperty = (index: number, field: string, value: string) => {
    const updated = [...data.additionalProperties];
    updated[index] = { ...updated[index], [field]: value };
    update('additionalProperties', updated);
  };

  const removeProperty = (index: number) => {
    update('additionalProperties', data.additionalProperties.filter((_, i) => i !== index));
  };

  const addDog = () => {
    update('dogDetails', [...data.dogDetails, { breed: '', weight: '', biteHistory: '' }]);
  };

  const updateDog = (index: number, field: string, value: string) => {
    const updated = [...data.dogDetails];
    updated[index] = { ...updated[index], [field]: value };
    update('dogDetails', updated);
  };

  const removeDog = (index: number) => {
    update('dogDetails', data.dogDetails.filter((_, i) => i !== index));
  };

  const addWatercraft = () => {
    update('watercraftDetails', [...data.watercraftDetails, { type: '', length: '', horsepower: '', insured: '' }]);
  };

  const updateWatercraft = (index: number, field: string, value: string) => {
    const updated = [...data.watercraftDetails];
    updated[index] = { ...updated[index], [field]: value };
    update('watercraftDetails', updated);
  };

  const removeWatercraft = (index: number) => {
    update('watercraftDetails', data.watercraftDetails.filter((_, i) => i !== index));
  };

  const toggleStaffRole = (role: string) => {
    const existing = data.staffDetails.find(s => s.role === role);
    if (existing) {
      update('staffDetails', data.staffDetails.filter(s => s.role !== role));
    } else {
      update('staffDetails', [...data.staffDetails, { role, fullOrPart: '', w2Or1099: '', count: '1' }]);
    }
  };

  const updateStaffDetail = (role: string, field: string, value: string) => {
    const updated = data.staffDetails.map(s => s.role === role ? { ...s, [field]: value } : s);
    update('staffDetails', updated);
  };

  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case 'A':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="Primary Named Insured" value={data.primaryInsured} onChange={v => update('primaryInsured', v)} placeholder="Full legal name" />
              <TextInput label="Spouse / Domestic Partner" value={data.spousePartner} onChange={v => update('spousePartner', v)} placeholder="If applicable" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Household Members</label>
                <button onClick={addHouseholdMember} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">+ Add Member</button>
              </div>
              {data.householdMembers.map((member, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <input className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Name" value={member.name} onChange={e => updateHouseholdMember(i, 'name', e.target.value)} />
                  <input className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Relationship" value={member.relationship} onChange={e => updateHouseholdMember(i, 'relationship', e.target.value)} />
                  <input className="col-span-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Age" value={member.age} onChange={e => updateHouseholdMember(i, 'age', e.target.value)} />
                  <select className="col-span-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={member.driver} onChange={e => updateHouseholdMember(i, 'driver', e.target.value)}>
                    <option value="">Driver?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <select className="col-span-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={member.studentAway} onChange={e => updateHouseholdMember(i, 'studentAway', e.target.value)}>
                    <option value="">Student Away?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <button onClick={() => removeHouseholdMember(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <TextInput label="Primary Residence Address" value={data.primaryAddress} onChange={v => update('primaryAddress', v)} placeholder="Street address" />
              </div>
              <TextInput label="State" value={data.primaryState} onChange={v => update('primaryState', v)} placeholder="State" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Own or Rent?</label>
              <RadioGroup name="ownOrRent" value={data.ownOrRent} onChange={v => update('ownOrRent', v)}
                options={[{ value: 'own', label: 'Own' }, { value: 'rent', label: 'Rent' }]} columns={2} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Properties</label>
                <button onClick={addProperty} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">+ Add Property</button>
              </div>
              {data.additionalProperties.map((prop, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <input className="col-span-4 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Address" value={prop.address} onChange={e => updateProperty(i, 'address', e.target.value)} />
                  <input className="col-span-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="State" value={prop.state} onChange={e => updateProperty(i, 'state', e.target.value)} />
                  <select className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={prop.type} onChange={e => updateProperty(i, 'type', e.target.value)}>
                    <option value="">Type</option>
                    <option value="primary">Primary</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="rental">Rental</option>
                    <option value="vacant">Vacant</option>
                  </select>
                  <select className="col-span-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={prop.ownedOrRented} onChange={e => updateProperty(i, 'ownedOrRented', e.target.value)}>
                    <option value="">Own/Rent</option>
                    <option value="owned">Owned</option>
                    <option value="rented">Rented</option>
                  </select>
                  <button onClick={() => removeProperty(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
              {data.additionalProperties.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No additional properties. Click "+ Add Property" if applicable.</p>
              )}
            </div>
          </div>
        );

      case 'B':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Home-Based Business</label>
              <RadioGroup name="homeBasedBusiness" value={data.homeBasedBusiness} onChange={v => update('homeBasedBusiness', v)} options={[
                { value: 'none', label: 'Not Applicable — No business activity from home' },
                { value: 'hobby', label: 'Hobby or occasional activity (under $5,000 annual revenue)' },
                { value: 'sole_prop', label: 'Active sole proprietorship or freelance' },
                { value: 'llc', label: 'LLC or business entity operating from home' },
                { value: 'professional', label: 'Professional practice from home (law, medicine, consulting, financial advisory)' },
              ]} />
              {data.homeBasedBusiness && data.homeBasedBusiness !== 'none' && (
                <div className="mt-2">
                  <TextInput label="Describe business activity" value={data.homeBusinessDescription} onChange={v => update('homeBusinessDescription', v)} placeholder="Brief description..." />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Family Office</label>
              <RadioGroup name="familyOffice" value={data.familyOffice} onChange={v => update('familyOffice', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'informal', label: 'Informal family wealth management (no separate entity)' },
                { value: 'formal', label: 'Formal family office (separate LLC or entity)' },
                { value: 'formal_employees', label: 'Family office with non-family employees' },
              ]} />
              {data.familyOffice && data.familyOffice !== 'none' && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Does the family office have its own insurance program (D&O, E&O, EPL)?</label>
                  <RadioGroup name="familyOfficeInsurance" value={data.familyOfficeInsurance} onChange={v => update('familyOfficeInsurance', v)}
                    options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} columns={3} />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Professional Licenses</label>
              <RadioGroup name="professionalLicenses" value={data.professionalLicenses} onChange={v => update('professionalLicenses', v)} options={[
                { value: 'none', label: 'Not Applicable — No professional licenses' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.professionalLicenses === 'yes' && (
                <div className="mt-2">
                  <TextInput label="License type(s)" value={data.licenseTypes} onChange={v => update('licenseTypes', v)} placeholder="e.g., Medical, Legal, CPA, Real Estate..." />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rental Property Activity</label>
              <RadioGroup name="rentalActivity" value={data.rentalActivity} onChange={v => update('rentalActivity', v)} options={[
                { value: 'none', label: 'Not Applicable — No rental activity' },
                { value: 'long_term', label: 'Long-term rental (annual lease)' },
                { value: 'short_term', label: 'Short-term rental (Airbnb, VRBO, or similar)' },
                { value: 'room', label: 'Room rental within primary residence' },
                { value: 'both', label: 'Both long-term and short-term rental' },
              ]} />
              {data.rentalActivity && data.rentalActivity !== 'none' && (
                <div className="mt-2">
                  <TextInput label="Number of rental properties" value={data.rentalPropertyCount} onChange={v => update('rentalPropertyCount', v)} placeholder="e.g., 2" />
                </div>
              )}
            </div>
          </div>
        );

      case 'C':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nonprofit Board Service</label>
              <RadioGroup name="nonprofitBoard" value={data.nonprofitBoard} onChange={v => update('nonprofitBoard', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'one', label: 'Yes — One organization' },
                { value: 'multiple', label: 'Yes — Two or more organizations' },
              ]} />
              {data.nonprofitBoard && data.nonprofitBoard !== 'none' && (
                <div className="mt-2 space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Does each organization carry its own D&O insurance?</label>
                  <RadioGroup name="nonprofitDO" value={data.nonprofitDO} onChange={v => update('nonprofitDO', v)}
                    options={[{ value: 'yes_all', label: 'Yes, all' }, { value: 'some', label: 'Some do, some don\'t' }, { value: 'no_unknown', label: 'No / Unknown' }]} columns={3} />
                  <TextInput label="Organization name(s) and your role (optional)" value={data.nonprofitDetails} onChange={v => update('nonprofitDetails', v)} placeholder="e.g., Local Food Bank - Board Treasurer" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">For-Profit Board Service</label>
              <RadioGroup name="forProfitBoard" value={data.forProfitBoard} onChange={v => update('forProfitBoard', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'private', label: 'Yes — Private company' },
                { value: 'public', label: 'Yes — Public company' },
                { value: 'multiple', label: 'Yes — Multiple companies' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Community Association Board Service</label>
              <RadioGroup name="communityAssociationBoard" value={data.communityAssociationBoard} onChange={v => update('communityAssociationBoard', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.communityAssociationBoard === 'yes' && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Does the association carry its own D&O insurance?</label>
                  <RadioGroup name="communityAssociationDO" value={data.communityAssociationDO} onChange={v => update('communityAssociationDO', v)}
                    options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} columns={3} />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Other Advisory or Fiduciary Roles</label>
              <RadioGroup name="otherFiduciaryRoles" value={data.otherFiduciaryRoles} onChange={v => update('otherFiduciaryRoles', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.otherFiduciaryRoles === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe role(s)" value={data.otherFiduciaryDescription} onChange={v => update('otherFiduciaryDescription', v)} placeholder="e.g., Trust trustee, estate executor..." />
                </div>
              )}
            </div>
          </div>
        );

      case 'D':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Coaching and Athletic Instruction</label>
              <RadioGroup name="coaching" value={data.coaching} onChange={v => update('coaching', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'volunteer', label: 'Volunteer coaching (youth or adult sports)' },
                { value: 'paid', label: 'Paid coaching or personal training' },
                { value: 'instruction', label: 'Martial arts, yoga, or fitness instruction' },
              ]} />
              {data.coaching && data.coaching !== 'none' && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Does the activity involve minors (under 18)?</label>
                  <RadioGroup name="coachingMinors" value={data.coachingMinors} onChange={v => update('coachingMinors', v)}
                    options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} columns={2} />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">General Volunteer Work</label>
              <RadioGroup name="volunteerWork" value={data.volunteerWork} onChange={v => update('volunteerWork', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'occasional', label: 'Occasional volunteering (events, drives, community service)' },
                { value: 'regular', label: 'Regular volunteer commitment (weekly or more)' },
                { value: 'supervisory', label: 'Volunteer role with supervisory responsibility' },
              ]} />
              {data.volunteerWork && data.volunteerWork !== 'none' && (
                <div className="mt-2">
                  <TextInput label="Describe (optional)" value={data.volunteerDescription} onChange={v => update('volunteerDescription', v)} placeholder="Brief description..." />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mentoring or Tutoring</label>
              <RadioGroup name="mentoring" value={data.mentoring} onChange={v => update('mentoring', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'adults', label: 'Yes — Adults' },
                { value: 'minors', label: 'Yes — Minors' },
                { value: 'both', label: 'Yes — Both' },
              ]} />
            </div>
          </div>
        );

      case 'E':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Political Activity</label>
              <RadioGroup name="politicalActivity" value={data.politicalActivity} onChange={v => update('politicalActivity', v)} options={[
                { value: 'none', label: 'Not Applicable — No political activity beyond voting' },
                { value: 'donor', label: 'Donor only (financial contributions)' },
                { value: 'volunteer', label: 'Active volunteer or campaign role' },
                { value: 'officer', label: 'Campaign officer, treasurer, or committee member' },
                { value: 'fundraiser', label: 'Political fundraiser host' },
                { value: 'candidate', label: 'Candidate for office' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Elected or Appointed Public Office</label>
              <RadioGroup name="publicOffice" value={data.publicOffice} onChange={v => update('publicOffice', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'advisory', label: 'Advisory board or commission member' },
                { value: 'local', label: 'Elected local office (town council, school board, etc.)' },
                { value: 'state_federal', label: 'Elected state or federal office' },
                { value: 'appointed', label: 'Appointed government position' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Political Activism</label>
              <RadioGroup name="politicalActivism" value={data.politicalActivism} onChange={v => update('politicalActivism', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'occasional', label: 'Occasional participation' },
                { value: 'regular', label: 'Regular participation' },
                { value: 'leadership', label: 'Leadership role in advocacy organization' },
              ]} />
            </div>
          </div>
        );

      case 'F':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Do you employ any persons who work in or around your home(s)?</label>
              <RadioGroup name="householdStaff" value={data.householdStaff} onChange={v => update('householdStaff', v)} options={[
                { value: 'none', label: 'Not Applicable — No household employees' },
                { value: 'yes', label: 'Yes' },
              ]} />
            </div>

            {data.householdStaff === 'yes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select applicable roles:</label>
                <div className="space-y-2">
                  {STAFF_ROLES.map(role => {
                    const detail = data.staffDetails.find(s => s.role === role);
                    const isSelected = !!detail;
                    return (
                      <div key={role} className={`rounded-lg border p-3 transition-colors ${isSelected ? 'border-indigo-300 dark:border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleStaffRole(role)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{role}</span>
                        </label>
                        {isSelected && (
                          <div className="mt-2 grid grid-cols-3 gap-3 pl-6">
                            <select className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white"
                              value={detail?.fullOrPart || ''} onChange={e => updateStaffDetail(role, 'fullOrPart', e.target.value)}>
                              <option value="">FT / PT</option>
                              <option value="full_time">Full-Time</option>
                              <option value="part_time">Part-Time</option>
                            </select>
                            <select className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white"
                              value={detail?.w2Or1099 || ''} onChange={e => updateStaffDetail(role, 'w2Or1099', e.target.value)}>
                              <option value="">W-2 / 1099</option>
                              <option value="w2">W-2 Employee</option>
                              <option value="1099">1099 Contractor</option>
                            </select>
                            <input type="number" min="1" className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white"
                              placeholder="How many" value={detail?.count || ''} onChange={e => updateStaffDetail(role, 'count', e.target.value)} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.householdStaff === 'yes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Workers' Compensation Coverage</label>
                <RadioGroup name="workersComp" value={data.workersComp} onChange={v => update('workersComp', v)} options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'unknown', label: 'Unknown' },
                ]} columns={3} />
              </div>
            )}
          </div>
        );

      case 'G':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dogs</label>
              <RadioGroup name="dogs" value={data.dogs} onChange={v => {
                update('dogs', v);
                if (v === 'yes' && data.dogDetails.length === 0) addDog();
              }} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.dogs === 'yes' && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Dog details</span>
                    <button onClick={addDog} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">+ Add Dog</button>
                  </div>
                  {data.dogDetails.map((dog, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                      <input className="col-span-4 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Breed" value={dog.breed} onChange={e => updateDog(i, 'breed', e.target.value)} />
                      <input className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Weight (lbs)" value={dog.weight} onChange={e => updateDog(i, 'weight', e.target.value)} />
                      <select className="col-span-4 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={dog.biteHistory} onChange={e => updateDog(i, 'biteHistory', e.target.value)}>
                        <option value="">Bite history?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      <button onClick={() => removeDog(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Horses</label>
              <RadioGroup name="horses" value={data.horses} onChange={v => update('horses', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'personal', label: 'Yes — Personal use only' },
                { value: 'commercial', label: 'Yes — Used for lessons, boarding, or commercial activity' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Other Animals (exotic, farm, or unusual)</label>
              <RadioGroup name="otherAnimals" value={data.otherAnimals} onChange={v => update('otherAnimals', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.otherAnimals === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe" value={data.otherAnimalsDescription} onChange={v => update('otherAnimalsDescription', v)} placeholder="e.g., 3 goats, 1 pot-bellied pig..." />
                </div>
              )}
            </div>
          </div>
        );

      case 'H':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Watercraft</label>
              <RadioGroup name="watercraft" value={data.watercraft} onChange={v => {
                update('watercraft', v);
                if (v === 'yes' && data.watercraftDetails.length === 0) addWatercraft();
              }} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.watercraft === 'yes' && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Watercraft details</span>
                    <button onClick={addWatercraft} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">+ Add Watercraft</button>
                  </div>
                  {data.watercraftDetails.map((craft, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                      <input className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Type" value={craft.type} onChange={e => updateWatercraft(i, 'type', e.target.value)} />
                      <input className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="Length" value={craft.length} onChange={e => updateWatercraft(i, 'length', e.target.value)} />
                      <input className="col-span-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" placeholder="HP" value={craft.horsepower} onChange={e => updateWatercraft(i, 'horsepower', e.target.value)} />
                      <select className="col-span-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" value={craft.insured} onChange={e => updateWatercraft(i, 'insured', e.target.value)}>
                        <option value="">Insured?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      <button onClick={() => removeWatercraft(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recreational Vehicles (ATVs, snowmobiles, golf carts, dirt bikes)</label>
              <RadioGroup name="recreationalVehicles" value={data.recreationalVehicles} onChange={v => update('recreationalVehicles', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.recreationalVehicles === 'yes' && (
                <div className="mt-2 space-y-2">
                  <TextInput label="Describe" value={data.recreationalVehiclesDescription} onChange={v => update('recreationalVehiclesDescription', v)} placeholder="e.g., 2 ATVs, 1 snowmobile..." />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Separate insurance?</label>
                    <RadioGroup name="recreationalVehiclesInsured" value={data.recreationalVehiclesInsured} onChange={v => update('recreationalVehiclesInsured', v)}
                      options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'some', label: 'Some' }]} columns={3} />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Aircraft</label>
              <RadioGroup name="aircraft" value={data.aircraft} onChange={v => update('aircraft', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'personal', label: 'Yes — Personal aircraft' },
                { value: 'fractional', label: 'Yes — Fractional ownership' },
                { value: 'rent_charter', label: 'Yes — Regularly rent/charter (pilot in command)' },
              ]} />
            </div>
          </div>
        );

      case 'I':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Firearms Ownership</label>
              <RadioGroup name="firearms" value={data.firearms} onChange={v => update('firearms', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'personal', label: 'Yes — Personal use (1-5 firearms)' },
                { value: 'collection', label: 'Yes — Collection (6+ firearms or significant value)' },
                { value: 'multiple_locations', label: 'Yes — Stored at multiple locations' },
              ]} />
              {data.firearms && data.firearms !== 'none' && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Do you regularly lend firearms to others or allow others to use them?</label>
                  <RadioGroup name="firearmsLend" value={data.firearmsLend} onChange={v => update('firearmsLend', v)}
                    options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} columns={2} />
                </div>
              )}
            </div>
          </div>
        );

      case 'J':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">International Travel</label>
              <RadioGroup name="internationalTravel" value={data.internationalTravel} onChange={v => update('internationalTravel', v)} options={[
                { value: 'none', label: 'Not Applicable — Rarely or never travel internationally' },
                { value: 'occasional', label: 'Occasional (1-2 trips per year)' },
                { value: 'frequent', label: 'Frequent (3+ trips per year)' },
                { value: 'extended', label: 'Extended stays abroad (1+ months)' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">International Property</label>
              <RadioGroup name="internationalProperty" value={data.internationalProperty} onChange={v => update('internationalProperty', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.internationalProperty === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Location(s)" value={data.internationalPropertyLocation} onChange={v => update('internationalPropertyLocation', v)} placeholder="e.g., Cabo San Lucas, Mexico" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">International Business Activity</label>
              <RadioGroup name="internationalBusiness" value={data.internationalBusiness} onChange={v => update('internationalBusiness', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.internationalBusiness === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe" value={data.internationalBusinessDescription} onChange={v => update('internationalBusinessDescription', v)} placeholder="Brief description..." />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">International Domestic Employees</label>
              <RadioGroup name="internationalEmployees" value={data.internationalEmployees} onChange={v => update('internationalEmployees', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
            </div>
          </div>
        );

      case 'K':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Social Media Presence</label>
              <RadioGroup name="socialMedia" value={data.socialMedia} onChange={v => update('socialMedia', v)} options={[
                { value: 'private', label: 'Private / Minimal — Accounts are private or rarely used' },
                { value: 'active_personal', label: 'Active Personal — Public accounts with regular personal posting' },
                { value: 'public_figure', label: 'Public Figure — Significant following or public-facing profile' },
                { value: 'content_creator', label: 'Content Creator — Produce regular content (blog, podcast, video, newsletter)' },
                { value: 'monetized', label: 'Monetized — Any social media income (ads, sponsorships, affiliate links)' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Public Profile</label>
              <RadioGroup name="publicProfile" value={data.publicProfile} onChange={v => update('publicProfile', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'local', label: 'Yes — Local/community level' },
                { value: 'regional', label: 'Yes — Regional/industry level' },
                { value: 'national', label: 'Yes — National/international level' },
              ]} />
            </div>
          </div>
        );

      case 'L':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Social Events (with alcohol served)</label>
              <RadioGroup name="socialEvents" value={data.socialEvents} onChange={v => update('socialEvents', v)} options={[
                { value: 'rarely', label: 'Rarely (a few times per year, small gatherings)' },
                { value: 'regularly', label: 'Regularly (monthly or more, or larger events)' },
                { value: 'large_frequent', label: 'Frequently host large events (20+ guests)' },
                { value: 'fundraisers', label: 'Host fundraisers, charity events, or political events at your home' },
              ]} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Staffing</label>
              <RadioGroup name="eventStaffing" value={data.eventStaffing} onChange={v => update('eventStaffing', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'occasionally', label: 'Occasionally' },
                { value: 'regularly', label: 'Regularly' },
              ]} />
            </div>
          </div>
        );

      case 'M':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pending or Threatened Litigation</label>
              <RadioGroup name="pendingLitigation" value={data.pendingLitigation} onChange={v => update('pendingLitigation', v)} options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.pendingLitigation === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe (confidential — used solely for coverage analysis)" value={data.pendingLitigationDescription} onChange={v => update('pendingLitigationDescription', v)} placeholder="Brief description..." multiline />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Prior Claims (past 5 years)</label>
              <RadioGroup name="priorClaims" value={data.priorClaims} onChange={v => update('priorClaims', v)} options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.priorClaims === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe" value={data.priorClaimsDescription} onChange={v => update('priorClaimsDescription', v)} placeholder="Brief description..." multiline />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trusts, LLCs, or Entity Ownership</label>
              <RadioGroup name="entityOwnership" value={data.entityOwnership} onChange={v => update('entityOwnership', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.entityOwnership === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Entity names and what they hold" value={data.entityDetails} onChange={v => update('entityDetails', v)} placeholder="e.g., Smith Family Trust — primary residence; Lakeshore LLC — rental property..." multiline />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Any Other Liability Concerns</label>
              <RadioGroup name="otherConcerns" value={data.otherConcerns} onChange={v => update('otherConcerns', v)} options={[
                { value: 'none', label: 'Not Applicable' },
                { value: 'yes', label: 'Yes' },
              ]} />
              {data.otherConcerns === 'yes' && (
                <div className="mt-2">
                  <TextInput label="Describe" value={data.otherConcernsDescription} onChange={v => update('otherConcernsDescription', v)} placeholder="Describe any activity, role, asset, or situation..." multiline />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Count total completed sections
  const completedSections = SECTIONS.filter(s => getSectionCompletionCount(s.id) === getSectionTotalFields(s.id)).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Personal Liability Exposure Questionnaire</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Complete applicable sections to enable comprehensive exposure mapping (Track 2 analysis). Sections marked "Not Applicable" are still valuable data.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            {completedSections} / {SECTIONS.length} sections
          </div>
        </div>
      </div>

      <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
        This questionnaire is optional. Skip it to run Track 1 (structural policy analysis) only. Complete it to unlock Track 2 (exposure mapping against your specific risk profile).
      </div>

      <div className="space-y-2">
        {SECTIONS.map(section => {
          const completed = getSectionCompletionCount(section.id);
          const total = getSectionTotalFields(section.id);
          const isExpanded = expandedSections.has(section.id);
          const isComplete = completed === total;

          return (
            <div key={section.id} className={`rounded-lg border transition-colors ${isComplete ? 'border-green-200 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'}`}>
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isComplete ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : completed > 0 ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                    {section.id}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{section.title}</span>
                    {completed > 0 && !isComplete && (
                      <span className="ml-2 text-xs text-gray-400">{completed}/{total}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isComplete && (
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700/50">
                  {renderSectionContent(section.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-gray-400 dark:text-gray-500 text-center pt-2">
        All information is used solely for insurance coverage analysis and is not shared with carriers or third parties.
      </div>
    </div>
  );
};

export default PersonalUmbrellaQuestionnaire;
